import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'validate.js';
import { Note } from 'api';
import { captableAction } from 'common/actions';
import { CAP_TABLE_REFERENCE_TITLE, CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { CAP_TABLE_PAGE_KEY } from 'common/constants/notes';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CellAnnotationProvider, useHydrateCaptableCellAnnotations } from 'components/CellAnnotationValueViewer';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { getNumberValue, getObjectValue, gridShortDate } from 'utilities';
import { CapTableProps } from './CapTable.props';
import CaptableTable from './components/captable-table/CaptableTable';
import { WidgetDrawerApplicationContext } from '../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { DocumentFootnotesProvider } from '../../../context';

const CapTable = ({
  selectedMeasurementDate,
  saveAsNewProforma,
  capTableId,
  onEditCaptable,
  isActiveTab = true,
}: CapTableProps) => {
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    saveSpreadsheetNotes,
    hasChanged: spreadsheetNoteHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const [isDisplayingRowNumbers, setIsDisplayingRowNumbers] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  const { id: currentCapTableId } = getObjectValue(captableInfo);

  const measurementDate = useMemo(
    () => (selectedMeasurementDate ? gridShortDate(selectedMeasurementDate.date) : undefined),
    [selectedMeasurementDate]
  );

  const fetchCapTable = useCallback(async () => {
    try {
      const response = await captableAction.verifyIfHasFinalAllocation(getNumberValue(currentCapTableId));
      setIsDisabled(!isEmpty(response));
    } catch (error: any) {
      setIsDisabled(false);
      throw new Error(error);
    }
  }, [currentCapTableId]);

  useEffect(() => {
    if (!currentCapTableId) {
      return;
    }

    fetchCapTable();
  }, [fetchCapTable, currentCapTableId]);

  useHydrateCaptableCellAnnotations(captableInfo);

  return (
    <DocumentFootnotesProvider>
      <CellAnnotationProvider>
        <CaptableTable
          onEditCaptable={onEditCaptable}
          measurementDate={measurementDate as string}
          selectedMeasurementDate={selectedMeasurementDate}
          saveAsNewProforma={saveAsNewProforma}
          addReferenceForExistingDocuments={addReferenceForExistingDocuments}
          isActiveTab={isActiveTab}
          filesToSave={filesToSave}
          setIsDisplayingRowNumbers={setIsDisplayingRowNumbers}
          setDocumentDisabledStatus={setIsDisabled}
          notes={notes as Note[]}
          saveNotes={saveNotes}
          saveSpreadsheetNotes={saveSpreadsheetNotes}
          notesHasChanged={notesHasChanged}
          spreadsheetNotes={spreadsheetNotes}
          spreadsheetNotesHasChanged={spreadsheetNoteHasChanged}
          widgetDrawerProps={{
            applicationContext: WidgetDrawerApplicationContext.CAP_TABLE,
            notesProps: {
              pageType: CAP_TABLE_REFERENCE_TYPE,
              pageTypeKey: CAP_TABLE_PAGE_KEY,
              pageTypeId: capTableId,
              notes,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            },
            documentReferencesProps: {
              selectedMeasurementDate,
              referenceType: CAP_TABLE_REFERENCE_TYPE,
              currentPage: CAP_TABLE_REFERENCE_TITLE,
              referencedFeatureId: capTableId,
              filesToSave,
              setFilesToSave,
              isDisplayingRowNumbers,
              isDisabled,
            },
            spreadsheetNotesProps: {
              pageTypeId: capTableId,
              pageType: CAP_TABLE_REFERENCE_TYPE,
              pageTypeKey: CAP_TABLE_PAGE_KEY,
              notes: spreadsheetNotes,
              setNotes: setSpreadsheetNotes,
              saveNotes: () => {
                saveSpreadsheetNotes();
                return undefined;
              },
              handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
            },
          }}
        />
      </CellAnnotationProvider>
    </DocumentFootnotesProvider>
  );
};

export default CapTable;
